import { useRef } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Eye, Loader2, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { useSMMGeneratorContext } from "../context/SMMGeneratorContext";

function formatDate(iso: string): string {
  if (!iso?.trim()) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy, hh:mm a");
}

function relativeTime(iso: string): string {
  if (!iso?.trim()) return "Date unavailable";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date unavailable";
  return formatDistanceToNow(d, { addSuffix: true });
}

export const ManifestHistoryPanel = () => {
  const {
    history,
    isLoadingHistory,
    isGenerating,
    previewEntry,
    isLoadingPreview,
    handlePreviewHistory,
    clearHistoryPreview,
  } = useSMMGeneratorContext();

  // Track which entry id triggered the current preview load so we can show
  // a per-card spinner without adding extra state to the context.
  const loadingIdRef = useRef<number | null>(null);

  if (isLoadingHistory) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <Clock className="h-5 w-5 text-muted-foreground/40 mb-2" />
        <p className="text-xs text-muted-foreground">
          No history yet. Versions appear after your manifest is generated.
        </p>
      </div>
    );
  }

  const isSelectedEntry = (id: number) => previewEntry?.id === id;
  const isLoadingEntry = (id: number) => isLoadingPreview && loadingIdRef.current === id;

  const onEntryClick = (id: number) => {
    // Clicking the currently-previewed entry dismisses the preview.
    if (isSelectedEntry(id)) {
      loadingIdRef.current = null;
      clearHistoryPreview();
      return;
    }
    loadingIdRef.current = id;
    handlePreviewHistory(id);
  };

  return (
    <div className="p-3 space-y-1.5">
      {history.map((entry, idx) => {
        const isCurrent = idx === 0;
        const isSelected = isSelectedEntry(entry.id);
        const isLoadingThis = isLoadingEntry(entry.id);
        const isDisabled = isGenerating;

        return (
          <div
            key={entry.id}
            onClick={() => !isCurrent && !isDisabled && onEntryClick(entry.id)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md border px-3 py-2 group transition-colors",
              isCurrent
                ? "border-primary/40 bg-primary/5"
                : isSelected
                  ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20 cursor-pointer"
                  : isDisabled
                    ? "border-border opacity-50 cursor-not-allowed"
                    : "border-border hover:bg-muted/30 cursor-pointer",
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground truncate">
                {formatDate(entry.created_at)}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {isCurrent ? "Current version" : relativeTime(entry.created_at)}
              </p>
            </div>

            {/* Action area — only for non-current entries */}
            {!isCurrent && (
              <span className="flex h-6 w-6 items-center justify-center shrink-0">
                {isLoadingThis ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          "flex h-6 w-6 items-center justify-center rounded-md transition-opacity",
                          "text-muted-foreground hover:text-foreground hover:bg-accent/40",
                          isSelected
                            ? "opacity-100 text-primary"
                            : "opacity-0 group-hover:opacity-100",
                        )}
                      >
                        <Eye className="h-3 w-3" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isSelected ? "Dismiss preview" : "Preview this version"}
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
